import React from 'react';
import type { MenuLink } from './menuItems';
import { MenuMegaProducts, type MegaProducts } from './MenuMegaProducts';

// The panel a drop-down opens when it holds more than a list — Sleek's mega menu, measured at 1440: the
// full width of the window under the header, the plain links large on the start edge, then a column per
// link that heads links of its own, picture cards after them, and the shop's products at the end edge.
//
// Which shape a drop-down takes is not a setting: a merchant who puts links under a link has asked for a
// column, one who gives a link a picture has asked for a card, and one who did neither gets the list.

interface MenuMegaPanelProps {
  id: string;
  open: boolean;
  links: readonly MenuLink[];
  /** The product row at the end edge, when the menu shows one. */
  products: MegaProducts | null;
  onLeave: () => void;
}

// Reports whether a drop-down's links ask for the wide panel: a column of links, or a picture.
export const wantsMegaPanel = (links: readonly MenuLink[]): boolean =>
  links.some((link) => link.image !== undefined || link.children.length > 0);

/** One card: the picture, and the words under it. */
const MegaCard: React.FC<{ link: MenuLink; onLeave: () => void }> = ({ link, onLeave }) => (
  <a className="xe-mega-card" href={link.href} onClick={onLeave}>
    <span className="xe-mega-card-frame">
      <img src={link.image} alt="" loading="lazy" decoding="async" />
    </span>
    <span className="xe-mega-card-label" dir="auto">{link.label}</span>
  </a>
);

// Draws one headed column: the link that heads it, then the links under it.
const MegaColumn: React.FC<{ link: MenuLink; onLeave: () => void }> = ({ link, onLeave }) => (
  <div className="xe-mega-column">
    {link.href ? <a className="xe-mega-heading" href={link.href} onClick={onLeave} dir="auto">{link.label}</a> : <span className="xe-mega-heading" dir="auto">{link.label}</span>}
    <ul>{link.children.map((leaf) => <li key={`${leaf.label}-${leaf.href ?? ''}`}><a className="xe-menu-sublink" href={leaf.href} onClick={onLeave} dir="auto">{leaf.label}</a></li>)}</ul>
  </div>
);

// Draws the wide panel: the plain links large, a column per heading link, the cards, and the products.
export const MenuMegaPanel: React.FC<MenuMegaPanelProps> = ({ id, open, links, products, onLeave }) => {
  const cards = links.filter((link) => link.image !== undefined);
  const columns = links.filter((link) => link.image === undefined && link.children.length > 0);
  const lead = links.filter((link) => link.image === undefined && link.children.length === 0);
  return (
    <div id={id} className="xe-menu-panel" data-mega data-open={open || undefined}>
      <div className="xe-mega-inner">
        {lead.length > 0 ? (
          <ul className="xe-mega-lead">
            {lead.map((link) => <li key={`${link.label}-${link.href ?? ''}`}><a href={link.href} onClick={onLeave} dir="auto">{link.label}</a></li>)}
          </ul>
        ) : null}
        {columns.map((link) => <MegaColumn key={`${link.label}-${link.href ?? ''}`} link={link} onLeave={onLeave} />)}
        {cards.length > 0 ? <div className="xe-mega-cards">{cards.map((link) => <MegaCard key={`${link.label}-${link.href ?? ''}`} link={link} onLeave={onLeave} />)}</div> : null}
        {open && products ? <MenuMegaProducts products={products} id={`${id}-products`} /> : null}
      </div>
    </div>
  );
};
